/** Bringing a stage's waves in, one after another (docs/spec/02 §4, §7). */

import { pathPoint } from './path';
import { makeEnemy } from './roster';
import { challengeSpeed } from './stages';
import type { Difficulty } from './tuning';
import { CHALLENGE, ENTRY } from './tuning';
import type { GameState, Spawn, Spawner } from './types';

export function newSpawner(): Spawner {
  return { pending: [], queue: [], wave: 0, t: 0, wait: 0 };
}

export function loadSpawner(sp: Spawner, waves: Spawn[][]): void {
  sp.pending = waves;
  sp.queue = [];
  sp.wave = 0;
  sp.t = 0;
  sp.wait = 0;
}

function spawn(s: GameState, sp: Spawn, d: Difficulty): void {
  const challenge = s.phase === 'challenge';
  const e = makeEnemy(s, sp.kind, 0, 0);
  e.variant = sp.variant;
  e.slotted = sp.slotted;
  e.row = sp.row;
  e.col = sp.col;
  e.wave = s.spawner.wave - 1;
  e.path = sp.path;
  e.side = sp.side;
  e.after = sp.after;
  e.speed = challenge ? challengeSpeed(s.challenge?.index ?? 1) : d.entrySpeed;
  const p = pathPoint(sp.path, 0, { x: 0, y: 0, h: 0 });
  e.x = p.x;
  e.y = p.y;
  e.heading = p.h;
  if (!challenge && d.entryFire && s.rng.oneIn(4)) {
    e.bombs = 1;
    e.bombTimer = 30 + s.rng.int(60);
  }
  s.enemies.push(e);
}

/** Every wave is in and everyone has found a place: the formation breathes and the dives begin. */
function settled(s: GameState): boolean {
  return !s.enemies.some((e) => e.state === 'entering' || (e.state === 'homing' && e.group === 0));
}

export function updateSpawner(s: GameState, d: Difficulty): void {
  const challenge = s.phase === 'challenge';
  if (s.phase !== 'entering' && !challenge) return;
  const sp = s.spawner;
  if (sp.queue.length > 0) {
    while (sp.queue.length > 0 && (sp.queue[0]?.at ?? 0) <= sp.t) {
      const next = sp.queue.shift();
      if (next) spawn(s, next, d);
    }
    sp.t = sp.queue.length > 0 ? sp.t + 1 : 0;
    return;
  }
  if (sp.pending.length === 0) {
    if (!challenge && settled(s)) {
      s.phase = 'battle';
      s.phaseT = 0;
      s.formation.settled = true;
    }
    return;
  }
  // Between waves: wait for the one before to be off its paths (or, in a challenging stage, gone).
  sp.t++;
  const clear = challenge ? s.enemies.length === 0 : !s.enemies.some((e) => e.state === 'entering');
  sp.wait = clear ? sp.wait + 1 : 0;
  const gap = challenge ? CHALLENGE.waveGap : ENTRY.waveGap;
  if (sp.wave === 0 || sp.wait >= gap || sp.t >= ENTRY.waveWaitMax) {
    sp.queue = sp.pending.shift() ?? [];
    sp.wave++;
    sp.t = 0;
    sp.wait = 0;
  }
}

/** A challenging stage is over when its last wave has come and gone. */
export function spawnerDone(s: GameState): boolean {
  return s.spawner.pending.length === 0 && s.spawner.queue.length === 0;
}
